import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { SeoHead } from "@/components/resources/SeoHead";
import { Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { publicCms } from "@/lib/cms";
import { ArrowRight, ChevronDown, Search, X } from "lucide-react";

const INDEX_META = {
  title: "Resources — AI Value Management guides, tools & templates | NetLift",
  description:
    "Practical guides, tools and templates for measuring the cost, savings, and return of AI adoption. Save Time. Show Return.",
  robots: "index,follow",
  canonical: "https://netlift.app/resources",
  og_title: "NetLift Resources",
  og_description: "Guides and tools for measuring AI value — cost, savings, and return.",
  og_url: "https://netlift.app/resources",
};

const SECTION_ORDER = ["ai-costs", "ai-roi", "tools", "departments", "industries", "ai-governance", "guides"];
const SECTION_ALIASES = { "tools-templates-calculators": "tools" };
const SECTION_LABELS = {
  "ai-costs": "AI Costs & Pricing",
  "ai-roi": "AI ROI",
  tools: "Tools & Calculators",
  departments: "By Department",
  industries: "By Industry",
  "ai-governance": "AI Governance",
  guides: "Guides",
};
const PREVIEW_COUNT = 6;
const HUB_TYPES = new Set(["pillar_page", "department_page", "industry_page"]);
export const RESOURCE_SECTIONS = new Set(SECTION_ORDER);

const SECTION_META = {
  "ai-costs": {
    h1: "AI Costs & Pricing",
    intro: "Vendor pricing, seat economics, TCO, API costs, and comparisons — kept current automatically.",
    title: "AI Costs & Pricing — vendor pricing, TCO & comparisons | NetLift",
    description: "Up-to-date pricing, total cost of ownership, and cost comparisons for the AI tools your team uses.",
  },
  "ai-roi": {
    h1: "AI ROI",
    intro: "How to measure, benchmark, and report the return on AI adoption.",
    title: "AI ROI — measurement guides & benchmarks | NetLift",
    description: "Practical guides for measuring and reporting the ROI of AI tools and AI-assisted work.",
  },
  tools: {
    h1: "Tools & Calculators",
    intro: "Free interactive calculators for AI ROI, break-even, TCO, and API costs.",
    title: "Free AI ROI Tools & Calculators | NetLift",
    description: "Interactive calculators for AI ROI, break-even hours, seat allocation, TCO, and API costs.",
  },
  departments: {
    h1: "AI ROI by Department",
    intro: "Department-specific playbooks: what to measure and typical returns by team.",
    title: "AI ROI by Department | NetLift",
    description: "AI ROI guides for marketing, sales, engineering, support, finance, HR, legal, and more.",
  },
  industries: {
    h1: "AI ROI by Industry",
    intro: "Industry-specific guidance on AI costs, value, and measurement.",
    title: "AI ROI by Industry | NetLift",
    description: "AI ROI guides for professional services, agencies, healthcare, SaaS, retail, and more.",
  },
  "ai-governance": {
    h1: "AI Governance",
    intro: "Policies, evaluation frameworks, and controls for responsible AI adoption.",
    title: "AI Governance — policy & evaluation guides | NetLift",
    description: "Guides on AI usage policy, tool evaluation, renewals, and governance for business teams.",
  },
  guides: {
    h1: "Guides",
    intro: "Foundational guides on AI value management.",
    title: "AI Value Guides | NetLift",
    description: "Foundational guides for measuring the cost, savings, and return of AI adoption.",
  },
};

function clusterLabel(section, cluster) {
  if (!cluster) return "More guides";
  if (section === "departments" || section === "industries") {
    let c = cluster.replace(/^AI ROI for /i, "").replace(/ AI ROI$/i, "")
      .replace(/ AI Spend Evaluation$/i, "").replace(/ and AI Governance Teams$/i, "");
    if (c === "SaaS") c = "SaaS and Software";
    return c;
  }
  return cluster;
}

export default function ResourcesIndex() {
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const activeSection = RESOURCE_SECTIONS.has(params.section) ? params.section : "";

  useEffect(() => {
    const hashSection = (location.hash || "").replace("#", "");
    if (hashSection && RESOURCE_SECTIONS.has(hashSection)) {
      navigate(`/resources/${hashSection}`, { replace: true });
    }
  }, [location.hash, navigate]);

  useEffect(() => {
    publicCms
      .list()
      .then(({ data }) => setPages(data.pages || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const terms = q.split(/\s+/);
    return pages.filter((p) => {
      const blob = `${p.title} ${p.summary_answer || ""} ${p.meta_description || ""} ${p.primary_keyword || ""} ${p.section || ""}`.toLowerCase();
      return terms.every((t) => blob.includes(t));
    });
  }, [pages, query]);

  const sections = useMemo(() => {
    const grouped = pages.reduce((acc, p) => {
      const key = SECTION_ALIASES[p.section] || p.section || "guides";
      (acc[key] = acc[key] || []).push(p);
      return acc;
    }, {});
    const keys = Object.keys(grouped).sort((a, b) => {
      const ia = SECTION_ORDER.indexOf(a);
      const ib = SECTION_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return keys.map((k) => [k, grouped[k]]);
  }, [pages]);

  const pillars = useMemo(() => pages.filter((p) => p.content_type === "pillar_page"), [pages]);

  const visibleSections = activeSection
    ? sections.filter(([key]) => key === activeSection)
    : sections;

  const setSection = (key) => {
    navigate(key ? `/resources/${key}` : "/resources");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const meta = activeSection
    ? {
        ...INDEX_META,
        title: SECTION_META[activeSection].title,
        description: SECTION_META[activeSection].description,
        canonical: `https://netlift.app/resources/${activeSection}`,
        og_title: SECTION_META[activeSection].h1,
        og_description: SECTION_META[activeSection].description,
        og_url: `https://netlift.app/resources/${activeSection}`,
      }
    : INDEX_META;

  const itemListSchema = pages.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "NetLift Resources",
        url: "https://netlift.app/resources",
        numberOfItems: pages.length,
        itemListElement: pages.slice(0, 100).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: p.title,
          url: `https://netlift.app${p.url_path}`,
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <SeoHead meta={meta} />
      {itemListSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      )}
      <Navbar />

      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 pt-16 sm:pt-20">
          <span className="text-xs font-semibold uppercase tracking-wide text-editorial">Resources</span>
          <h1 className="mt-2 max-w-2xl font-heading text-4xl font-semibold leading-tight text-navy sm:text-5xl">
            {activeSection ? SECTION_META[activeSection].h1 : "Measure the cost, savings, and return of AI."}
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-600">
            {activeSection
              ? SECTION_META[activeSection].intro
              : "Practical guides, tools, and templates to help you track AI-assisted work and show the return. Save Time. Show Return."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button className="bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")} data-testid="resources-hero-trial">
              Start Free Trial
            </Button>
            <Button variant="outline" onClick={() => navigate("/demo")} data-testid="resources-hero-sample">
              See Sample Report
            </Button>
          </div>
          <div className="relative mt-8 max-w-xl">
            <Search className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${pages.length || ""} guides, pricing pages, calculators…`}
              className="h-12 rounded-full border-slate-300 bg-white pl-11 pr-10 text-base"
              data-testid="resources-search-input"
            />
            {query ? (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="resources-search-clear"
              >
                <X className="h-5 w-5" />
              </button>
            ) : null}
          </div>
          <nav aria-label="Resource categories" className="-mx-6 mt-8 overflow-x-auto px-6 pb-5" data-testid="resources-category-nav">
            <div className="flex w-max gap-2">
              <CategoryPill
                label="All"
                count={pages.length}
                active={!activeSection}
                onClick={() => setSection("")}
                testId="resources-cat-all"
              />
              {sections.map(([key, items]) => (
                <CategoryPill
                  key={key}
                  label={SECTION_LABELS[key] || key.replace(/-/g, " ")}
                  count={items.length}
                  active={activeSection === key}
                  onClick={() => setSection(key)}
                  testId={`resources-cat-${key}`}
                />
              ))}
            </div>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        {loading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : pages.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="resources-empty">
            <h2 className="font-heading text-xl font-semibold text-navy">New resources coming soon</h2>
            <p className="mt-2 text-sm text-slate-500">
              We're publishing practical guides on measuring AI value. Check back shortly.
            </p>
            <Button className="mt-5 bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")}>
              Start Free Trial
            </Button>
          </div>
        ) : results ? (
          <section className="mb-12" data-testid="resources-search-results">
            <h2 className="mb-5 font-heading text-2xl font-semibold text-navy">
              {results.length} result{results.length === 1 ? "" : "s"} for “{query.trim()}”
            </h2>
            {results.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
                No resources match that search. Try a tool name (e.g. “Copilot”), a topic (“ROI”, “pricing”) or a team (“marketing”).
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((p) => <ResourceCard key={p.id} p={p} />)}
              </div>
            )}
          </section>
        ) : (
          <>
            {!activeSection && pillars.length > 0 && (
              <section className="mb-12" data-testid="resources-pillars">
                <h2 className="mb-2 font-heading text-2xl font-semibold text-navy">Start here</h2>
                <p className="mb-5 text-sm text-slate-500">Foundational guides that anchor everything else.</p>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {pillars.slice(0, 6).map((p) => <ResourceCard key={p.id} p={p} />)}
                </div>
              </section>
            )}
            {visibleSections.map(([section, items]) => {
              const isFiltered = Boolean(activeSection);
              if (isFiltered) return <ClusteredSection key={section} section={section} items={items} />;
              const isExpanded = expanded[section];
              return (
                <section key={section} id={section} className="mb-12 scroll-mt-24" data-testid={`resources-section-${section}`}>
                  <div className="mb-5 flex items-baseline justify-between gap-4">
                    <h2 className="font-heading text-2xl font-semibold text-navy">
                      {SECTION_LABELS[section] || section.replace(/-/g, " ")}
                      <span className="ml-2 align-middle text-sm font-normal text-slate-400">{items.length}</span>
                    </h2>
                    {items.length > PREVIEW_COUNT && (
                      <button
                        onClick={() => setSection(section)}
                        className="whitespace-nowrap text-sm font-medium text-editorial hover:underline"
                        data-testid={`resources-viewall-${section}`}
                      >
                        View all {items.length} →
                      </button>
                    )}
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((p, i) => (
                      <ResourceCard key={p.id} p={p} hidden={!isExpanded && i >= PREVIEW_COUNT} />
                    ))}
                  </div>
                  {!isExpanded && items.length > PREVIEW_COUNT && (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [section]: true }))}
                      className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-editorial hover:text-editorial"
                      data-testid={`resources-showmore-${section}`}
                    >
                      Show {items.length - PREVIEW_COUNT} more <ChevronDown className="h-4 w-4" />
                    </button>
                  )}
                </section>
              );
            })}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function ClusteredSection({ section, items }) {
  const groups = useMemo(() => {
    const byCluster = items.reduce((acc, p) => {
      const label = clusterLabel(section, p.cluster);
      (acc[label] = acc[label] || []).push(p);
      return acc;
    }, {});
    return Object.entries(byCluster)
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .map(([label, pages]) => [
        label,
        pages.sort((a, b) => (HUB_TYPES.has(b.content_type) ? 1 : 0) - (HUB_TYPES.has(a.content_type) ? 1 : 0)),
      ]);
  }, [section, items]);

  return (
    <section id={section} className="mb-12 scroll-mt-24" data-testid={`resources-section-${section}`}>
      <h2 className="mb-6 font-heading text-2xl font-semibold text-navy">
        {SECTION_LABELS[section] || section.replace(/-/g, " ")}
        <span className="ml-2 align-middle text-sm font-normal text-slate-400">{items.length}</span>
      </h2>
      {groups.map(([label, pages]) => (
        <div key={label} className="mb-10" data-testid={`resources-cluster-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h3 className="mb-4 border-b border-slate-200 pb-2 font-heading text-lg font-semibold text-slate-700">
            {label} <span className="text-sm font-normal text-slate-400">{pages.length}</span>
          </h3>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pages.map((p) => <ResourceCard key={p.id} p={p} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

function CategoryPill({ label, count, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-editorial bg-editorial text-white"
          : "border-slate-300 bg-white text-slate-600 hover:border-editorial hover:text-editorial"
      }`}
    >
      {label}
      <span className={`text-xs ${active ? "text-white/75" : "text-slate-400"}`}>{count}</span>
    </button>
  );
}

function ResourceCard({ p, hidden }) {
  return (
    <Link
      to={p.url_path}
      className={`group flex-col rounded-xl border border-slate-200 bg-white p-6 transition-colors hover:border-editorial ${hidden ? "hidden" : "flex"}`}
      data-testid="resource-card"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {(p.content_type || "article").replace(/_/g, " ")}
      </span>
      <h3 className="mt-2 font-heading text-lg font-semibold text-navy group-hover:text-editorial">
        {p.title}
      </h3>
      <p className="mt-2 line-clamp-3 flex-1 text-sm text-slate-500">
        {p.summary_answer || p.meta_description}
      </p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-editorial">
        Read <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
